import React, { useEffect, useState, useRef, useMemo } from "react";
import { createPortal } from 'react-dom';
import $ from 'jquery';
import Timeline from "react-calendar-timeline";
import 'react-calendar-timeline/dist/Timeline.scss';
import { Icon } from "@iconify/react";
import { getEvents, createEvents, updateEvents } from "../api/eventsAPI";
import { getEventStatus } from "../api/event-statusAPI";
import { getEventTypes } from "../api/event-typeAPI";
import { getPaginatedAssets } from "../api/assetAPI";
import { getPaginatedWorkOrders, updateWorkOrder } from "../api/work-orderAPI";
import { getWorkOrderStatus } from "../api/work-order-statusAPI";
import {
  getEventDelayByEventId,
  createEventDelay,
  updateEventDelay,
  deleteEventDelay,
} from "../api/event-delayAPI";
import { getEventDelayCodes } from "../api/event-delay-codeAPI";
import { useUserProfile } from "../masterLayout/MasterLayoutBody";
import { fetchData } from "../utils/apiHelpers";
import { validateForm } from "../utils/useValidator";
import { useTableFilters } from "../utils/useTableFilters";
import { useFormBuilder } from "../utils/useFormBuilder";
import { useValidator } from "../utils/useValidator";
import { computeDefaultDelayStartDate, validateDelayDateRange } from "../utils/eventDelayRules";
import { useSetting } from '../utils/AppSettingsContext';
import {
  getEventRange,
  hasWorkOrdersOutsideEventRange,
  buildCompleteWorkOrderPayload,
  areWorkOrdersCompletableForEvent,
} from '../utils/eventWorkOrderRules';
import "./VisualPlannerLayer.css";
import EventEditModal from "./EventEditModal";

const getBootstrapHex = (colorName) => {
  switch ((colorName || "").toLowerCase()) {
    case "primary":
      return "487fff";
    case "secondary":
      return "737373";
    case "success":
      return "22c55e";
    case "danger":
      return "dc2626";
    case "warning":
      return "eab308";
    case "info":
      return "0dcaf0";
    case "light":
      return "f8f9fa";
    case "dark":
      return "212529";
    case "alert":
      return "fd7e14";
    default:
      return null;
  }
};

// convert ISO date string to yyyy-mm-ddThh:mm format for input fields
const toLocalInputFormat = (isoString, withTime = true) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return withTime
    ? `${yyyy}-${mm}-${dd}T${hh}:${min}`
    : `${yyyy}-${mm}-${dd}`;
};

// display datetime as dd/mm/yy hh:mm
const formatDateTime = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  const datePart = date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
  const timePart = date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${datePart} ${timePart}`;
};

const calcDelayDuration = (d) =>
  d?.delayEndDate && d?.delayStartDate
    ?
    (new Date(d.delayEndDate) - new Date(d.delayStartDate)) /
    (1000 * 60 * 60)
    : 0;

const VisualPlannerLayer = () => {
  const containerRef = useRef(null);
  const ganttRef = useRef(null);
  const panRef = useRef({ isPanning: false, startX: 0, startScroll: 0 });
  const userProfile = useUserProfile();
  const completeEventWorkOrdersSetting = useSetting('complete_event_work_orders', false);
  const selectedItemStyle = { boxShadow: '0 0 0 2px #dc2626 inset' };

  const [isDragging, setIsDragging] = useState(false);
  const [split, setSplit] = useState(50); // percentage height for top section
  const [viewMode, setViewMode] = useState("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [visibleTime, setVisibleTime] = useState(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return { start: start.getTime(), end: end.getTime() };
  });
  const [timelineScrollValue, setTimelineScrollValue] = useState(500);
  const prevTimelineScrollValueRef = useRef(500);

  const [events, setEvents] = useState(null); // raw events
  const [tasks, setTasks] = useState(null); // timeline items
  const [groups, setGroups] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({});
  const [newEventErrors, setNewEventErrors] = useState({});
  const [assetOptions, setAssetOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [eventTypeOptions, setEventTypeOptions] = useState([]);

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [availableWorkOrders, setAvailableWorkOrders] = useState([]);
  const [assignedWorkOrders, setAssignedWorkOrders] = useState([]);
  const [dragWorkOrder, setDragWorkOrder] = useState(null);

  const [eventDelays, setEventDelays] = useState([]);
  const [eventComments, setEventComments] = useState([]);
  const [delayCodes, setDelayCodes] = useState([]);
  const [showDelayModal, setShowDelayModal] = useState(false);
  const [editingDelay, setEditingDelay] = useState(null);
  const [delayForm, setDelayForm] = useState({
    eventDelayCodeId: '',
    delayStartDate: '',
    delayEndDate: '',
  });
  const delayValidationFields = useMemo(
    () => [
      { field: 'eventDelayCodeId', label: 'Delay Code', required: true },
      { field: 'delayStartDate', label: 'Delay Start Date', required: true, type: 'datetime' },
      { field: 'delayEndDate', label: 'Delay End Date', required: true, type: 'datetime' },
    ],
    [],
  );
  const {
    errors: delayErrors,
    setErrors: setDelayErrors,
    validate: validateDelayForm,
  } = useValidator(delayValidationFields, delayForm);
  const [delayDuration, setDelayDuration] = useState(0);

  useEffect(() => {
    if (delayForm.delayEndDate && delayForm.delayStartDate) {
      const dur =
        (new Date(delayForm.delayEndDate) - new Date(delayForm.delayStartDate)) /
        (1000 * 60 * 60);
      setDelayDuration(Number.isFinite(dur) ? parseFloat(dur.toFixed(2)) : 0);
    } else {
      setDelayDuration(0);
    }
  }, [delayForm.delayStartDate, delayForm.delayEndDate]);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignOptions, setAssignOptions] = useState([]);
  const [assignSelected, setAssignSelected] = useState([]);
  const [assignSearch, setAssignSearch] = useState('');
  const [headerSearchContainer, setHeaderSearchContainer] = useState(null);
  const assignTableRef = useRef(null);
  const assignDtRef = useRef(null);
  const [workOrderStatuses, setWorkOrderStatuses] = useState([]);
  const [showEditWoModal, setShowEditWoModal] = useState(false);
  const [editingWo, setEditingWo] = useState(null);
  const [editWoForm, setEditWoForm] = useState({});
  const [editWoErrors, setEditWoErrors] = useState({});

  const editWoFields = useMemo(
    () => [
      {
        field: 'statusId',
        label: 'Status',
        type: 'select',
        size: 6,
        api: 'work-order-status',
        displayField: 'name',
        required: true,
        formatOptionLabel: (opt) => (
          <div className="d-flex align-items-center gap-2">
            <span className={`d-inline-block rounded-1 bg-${opt.bootstrapName}`} style={{ width: '10px', height: '10px' }}></span>
            {opt.label}
          </div>
        ),
      },
      { field: 'startedAt', label: 'Started At', type: 'datetime', size: 6 },
      { field: 'completedAt', label: 'Completed At', type: 'datetime', size: 6 },
      { field: 'failureId', label: 'Failure', type: 'select', size: 6, api: 'failure-codes', displayField: 'name' },
      { field: 'causeId', label: 'Cause', type: 'select', size: 6, api: 'cause-codes', displayField: 'name' },
      { field: 'symptom', label: 'Symptom', type: 'text', size: 6 },
      { field: 'notes', label: 'Completion Notes', type: 'textarea', size: 12 },
    ],
    []
  );

  const { renderField: renderEditWoField } = useFormBuilder(editWoFields, editWoForm, setEditWoForm, editWoErrors);

  useEffect(() => {
    fetchData(getWorkOrderStatus, {}, (data) => {
      setWorkOrderStatuses(
        (data || []).map((s) => ({
          value: s.id,
          label: s.name,
          bootstrapName: s.colours?.bootstrapName || 'secondary',
        }))
      );
    });
  }, []);

  useEffect(() => {
    const statusName = statusOptions.find((s) => String(s.value) === String(newEvent.eventStatus))?.label?.toLowerCase();
    if (statusName === 'complete') {
      setNewEvent((prev) => ({
        ...prev,
        startDate: prev.startDate || prev.plannedStartDate,
        endDate: prev.endDate || prev.plannedEndDate,
      }));
    } else if (statusName === 'in progress') {
      setNewEvent((prev) => ({
        ...prev,
        startDate: prev.startDate || prev.plannedStartDate,
      }));
    }
  }, [newEvent.eventStatus, statusOptions]);

  const filterConfig = useMemo(
    () => [
      {
        field: "assetId",
        type: "paginatedDropdown",
        label: "Asset",
        loadOptions: (input) => handleAssetSearch(input),
      },
      {
        field: "classId",
        type: "dropDown",
        label: "Asset Class",
        api: "class",
        displayField: "className",
        menuBarFilter: 1,
      },
      {
        field: "groupId",
        type: "dropDown",
        label: "Asset Group",
        api: "groups",
        displayField: "groupName",
        menuBarFilter: 1,
      },
      {
        field: "eventStatus",
        type: "dropDown",
        label: "Event Status",
        api: "event-status",
        displayField: "name",
        menuBarFilter: 1,
      },
      {
        field: "departmentId",
        type: "dropDown",
        label: "Department",
        api: "hierarchy-structures",
        displayField: "name",
      },
    ],
    []
  );

  const {
    appliedFilterParams,
    renderFilterControls,
  } = useTableFilters(filterConfig);


  const loadEvents = async () => {
    try {
      const eventsResp = await getEvents();
      setEvents(eventsResp);
    } catch (err) {
      console.error('Error loading visual planner data', err);
    }
  };

  const loadWorkOrdersForEvent = async (ev) => {
    if (!ev) {
      setAvailableWorkOrders([]);
      setAssignedWorkOrders([]);
      setEventDelays([]);
      return;
    }
    try {
      const resp = await getPaginatedWorkOrders({
        page: 1,
        pageSize: 1000,
        assetId: [ev.assetId],
      });
      const data = resp.data || resp;
      const available = data.filter((w) => !w.eventId);
      const assigned = data.filter((w) => w.eventId === ev.id);
      setAvailableWorkOrders(available);
      setAssignedWorkOrders(assigned);
      setEvents((prev) =>
        (prev || []).map((e) =>
          e.id === ev.id ? { ...e, workOrderCount: assigned.length } : e
        )
      );
      await loadEventDelays(ev.id);
    } catch (err) {
      console.error('Error loading work orders', err);
    }
  };

  const loadEventDelays = async (eventId) => {
    if (!eventId) {
      setEventDelays([]);
      return;
    }
    try {
      const resp = await getEventDelayByEventId(eventId);
      const data = resp.data || resp;
      setEventDelays(data);
    } catch (err) {
      console.error('Error loading event delays', err);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);


  useEffect(() => {
    if (!events) return;
    let filtered = events;
    if (appliedFilterParams.assetId?.length) {
      filtered = filtered.filter((e) => appliedFilterParams.assetId.includes(e.assetId));
    }
    if (appliedFilterParams.groupId?.length) {
      filtered = filtered.filter((e) => appliedFilterParams.groupId.includes(e.asset?.groupId));
    }
    if (appliedFilterParams.classId?.length) {
      filtered = filtered.filter((e) => appliedFilterParams.classId.includes(e.asset?.group?.classId));
    }
    if (appliedFilterParams.eventStatus?.length) {
      filtered = filtered.filter((e) => appliedFilterParams.eventStatus.includes(e.eventStatus));
    }
    if (appliedFilterParams.departmentId?.length) {
      filtered = filtered.filter((e) => appliedFilterParams.departmentId.includes(e.asset?.assetDepartmentId));
    }

    const groupMap = {};
    const groupsData = [];

    const taskData = filtered.map((e) => {
      const statusName = e.eventStatus2?.name?.toLowerCase();
      const colorName =
        e.eventStatus?.colours?.bootstrapName ||
        e.eventStatus2?.colours?.bootstrapName;
      const hex = getBootstrapHex(colorName);
      const delayTotal = (e.tblEventDelays || []).reduce(
        (sum, d) => sum + calcDelayDuration(d),
        0,
      );

      let start = new Date(e.plannedStartDate);
      let end = new Date(e.plannedEndDate);
      if (statusName === 'in progress') {
        start = new Date(e.startDate);
        end = new Date(e.plannedEndDate);
      } else if (statusName === 'complete') {
        start = new Date(e.startDate);
        end = new Date(e.endDate);
      }
      end = new Date(end.getTime() + delayTotal * 60 * 60 * 1000);

      if (!groupMap[e.assetId]) {
        groupMap[e.assetId] = {
          id: e.assetId,
          title: `${e.asset.assetNumber} – ${e.asset.assetDescription}`,
        };
        groupsData.push(groupMap[e.assetId]);
      }

      const baseStyle = hex ? { background: `#${hex}`, color: '#000' } : {};
      const style =
        selectedEvent && selectedEvent.id === e.id
          ? { ...baseStyle, ...selectedItemStyle }
          : baseStyle;

      return {
        id: String(e.id),
        group: e.assetId,
        title: `${e.name} (${e.workOrderCount || 0})`,
        start_time: start,
        end_time: end,
        itemProps: { style },
      };
    });
    setGroups(groupsData);
    setTasks(taskData);
  }, [events, appliedFilterParams, selectedEvent]);


  const adjustDate = (amount) => {
    const d = new Date(currentDate);
    if (viewMode === "day") d.setDate(d.getDate() + amount);
    else if (viewMode === "week") d.setDate(d.getDate() + amount * 7);
    else if (viewMode === "month") d.setMonth(d.getMonth() + amount);
    else if (viewMode === "year") d.setFullYear(d.getFullYear() + amount);
    setCurrentDate(d);
  };

  const getVisibleTimeRange = (mode = viewMode, date = currentDate) => {
    const d = new Date(date);
    let start;
    let end;
    switch (mode) {
      case 'day':
        start = new Date(d);
        start.setHours(0, 0, 0, 0);
        end = new Date(start);
        end.setDate(end.getDate() + 1);
        break;
      case 'week':
        start = new Date(d);
        start.setDate(start.getDate() - start.getDay());
        start.setHours(0, 0, 0, 0);
        end = new Date(start);
        end.setDate(end.getDate() + 7);
        break;
      case 'month':
        start = new Date(d.getFullYear(), d.getMonth(), 1);
        end = new Date(d.getFullYear(), d.getMonth() + 1, 1);
        break;
      case 'year':
        start = new Date(d.getFullYear(), 0, 1);
        end = new Date(d.getFullYear() + 1, 0, 1);
        break;
      default:
        start = new Date(d.getFullYear(), d.getMonth(), 1);
        end = new Date(d.getFullYear(), d.getMonth() + 1, 1);
        break;
    }
    return { start, end };
  };

  useEffect(() => {
    const range = getVisibleTimeRange(viewMode, currentDate);
    setVisibleTime({ start: range.start.getTime(), end: range.end.getTime() });
    setTimelineScrollValue(500);
    prevTimelineScrollValueRef.current = 500;
  }, [viewMode, currentDate]);

  const getTimelineScrollStepMs = (mode) => {
    switch (mode) {
      case 'day':
        return 15 * 60 * 1000;
      case 'week':
        return 60 * 60 * 1000;
      case 'month':
        return 6 * 60 * 60 * 1000;
      case 'year':
        return 24 * 60 * 60 * 1000;
      default:
        return 60 * 60 * 1000;
    }
  };

  const handleTimelineScrollBar = (e) => {
    const nextValue = Number(e.target.value);
    const delta = nextValue - prevTimelineScrollValueRef.current;
    prevTimelineScrollValueRef.current = nextValue;
    setTimelineScrollValue(nextValue);
    if (delta === 0) return;

    const shiftMs = delta * getTimelineScrollStepMs(viewMode);
    setVisibleTime((prev) => ({
      start: prev.start + shiftMs,
      end: prev.end + shiftMs,
    }));
  };

  useEffect(() => {
    loadWorkOrdersForEvent(selectedEvent);
  }, [selectedEvent]);

  const startDrag = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const stopDrag = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const onDrag = (e) => {
    if (!isDragging || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const pct = ((e.clientY - rect.top) / rect.height) * 100;
    if (pct > 10 && pct < 90) {
      setSplit(pct);
    }
  };

  const handleGanttMouseDown = (e) => {
    if (e.target.closest('.rct-item')) return;
    if (ganttRef.current) {
      panRef.current = {
        isPanning: true,
        startX: e.clientX,
        startScroll: ganttRef.current.scrollLeft,
      };
    }
  };

  const handleGanttMouseMove = (e) => {
    if (!panRef.current.isPanning || !ganttRef.current) return;
    ganttRef.current.scrollLeft = panRef.current.startScroll - (e.clientX - panRef.current.startX);
  };

  const stopGanttPan = () => {
    panRef.current.isPanning = false;
  };

  const roundToHour = (d) => {
    const date = new Date(d);
    date.setMinutes(0, 0, 0);
    return date;
  };

  const openModal = async (event = null) => {
    setEditingEvent(event);
    const start = roundToHour(event?.plannedStartDate || new Date());
    const end = new Date(start.getTime() + 4 * 60 * 60 * 1000);
    setNewEvent({
      name: event?.name || "",
      assetId: event?.assetId || null,
      eventTypeId: event?.eventTypeId || '',
      plannedStartDate: toLocalInputFormat(start),
      plannedEndDate: toLocalInputFormat(event?.plannedEndDate || end),
      startDate: toLocalInputFormat(event?.startDate),
      endDate: toLocalInputFormat(event?.endDate),
      eventStatus: event?.eventStatus || "",
      systemId: event?.systemId ?? null,
    });
    setNewEventErrors({});
    try {
      const [assetResp, statusResp, codesResp, typeResp] = await Promise.all([
        getPaginatedAssets({ page: 1, pageSize: 10 }),
        fetchData(getEventStatus, { apiName: 'event-status' }),
        fetchData(getEventDelayCodes, { apiName: 'event-delay-codes' }),
        fetchData(getEventTypes, { apiName: 'event-types' }),
      ]);
      const assetsArr = assetResp.data || assetResp;
      setAssetOptions(
        assetsArr.map((a) => ({
          value: a.id,
          label: `${a.assetNumber} – ${a.assetDescription}`,
        }))
      );
      setStatusOptions(
        (statusResp || []).map((s) => ({ value: s.id, label: s.name }))
      );
      setEventTypeOptions(
        (typeResp || []).map((t) => ({ value: t.id, label: `${t.code} - ${t.name}` }))
      );
      setDelayCodes(codesResp.data || codesResp);
      if (event?.id) {
        setEventComments([]);
        await Promise.all([loadEventDelays(event.id), loadWorkOrdersForEvent(event)]);
      } else {
        setEventDelays([]);
        setEventComments([]);
        setAssignedWorkOrders([]);
      }
    } catch (err) {
      console.error("Error loading modal data", err);
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    const statusId = Number(newEvent?.eventStatus);
    const eventTypeId = Number(newEvent?.eventTypeId);
    const isBreakdownEvent = eventTypeId === 2;
    const isInProgress = statusId === 1;
    const isComplete = statusId === 2;
    const isCancelled = statusId === 3;

    const plannedStartRequired = !(
      (isInProgress && !isBreakdownEvent) ||
      (isComplete && !isBreakdownEvent) ||
      isCancelled
    );

    const plannedEndRequired = !(
      (isComplete && !isBreakdownEvent) || isCancelled
    );

    const baseFields = [
      { field: 'name', label: 'Name', required: true },
      { field: 'assetId', label: 'Asset', required: true },
      { field: 'eventTypeId', label: 'Event Type', required: true },
      {
        field: 'plannedStartDate',
        label: 'Planned Start Date',
        required: plannedStartRequired,
      },
      {
        field: 'plannedEndDate',
        label: 'Planned End Date',
        required: plannedEndRequired,
      },
      { field: 'eventStatus', label: 'Status', required: true },
    ];
    const statusName = statusOptions.find((s) => String(s.value) === String(newEvent.eventStatus))?.label?.toLowerCase();
    let errs = validateForm(baseFields, newEvent);
    const now = new Date();
    if (statusName === 'in progress' || statusName === 'complete') {
      if (!newEvent.startDate) {
        errs.startDate = 'Start date is required';
      } else if (new Date(newEvent.startDate) > now) {
        errs.startDate = 'Start date must be in the past';
      }
    }
    if (statusName === 'complete') {
      if (!newEvent.endDate) {
        errs.endDate = 'End date is required';
      } else if (new Date(newEvent.endDate) > now) {
        errs.endDate = 'End date must be in the past';
      }
      if (newEvent.startDate && newEvent.endDate && new Date(newEvent.startDate) > new Date(newEvent.endDate)) {
        errs.startDate = 'Start date must be before end date';
      }
    }
    setNewEventErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const { eventStart, eventEnd } = getEventRange(newEvent);
    if (statusId === 2 && completeEventWorkOrdersSetting === true) {
      const canComplete = areWorkOrdersCompletableForEvent(
        assignedWorkOrders,
        eventStart,
        eventEnd,
      );
      if (!canComplete) {
        window.alert(
          'All event Work Orders must have status Complete, Cancelled or otherwise removed from the event to complete the event.',
        );
        return;
      }
    }

    const start = newEvent.startDate
      ? new Date(newEvent.startDate)
      : new Date(newEvent.plannedStartDate);
    const end = newEvent.endDate
      ? new Date(newEvent.endDate)
      : new Date(newEvent.plannedEndDate);
    const durationHours = Math.abs(end - start) / (1000 * 60 * 60);
    const delayTotal = eventDelays.reduce(
      (sum, d) => sum + calcDelayDuration(d),
      0,
    );
    if (delayTotal >= durationHours) {
      alert('Total delay duration must be less than the event duration');
      return;
    }
    const normalizedEventStatus = Number(newEvent.eventStatus) || null;
    const normalizedEndDate =
      normalizedEventStatus === 1
        ? null
        : newEvent.endDate
          ? new Date(newEvent.endDate).toISOString()
          : null;

    const payload = {
      name: newEvent.name,
      assetId: Number(newEvent.assetId),
      eventTypeId: Number(newEvent.eventTypeId),
      plannedStartDate: new Date(newEvent.plannedStartDate).toISOString(),
      plannedEndDate: new Date(newEvent.plannedEndDate).toISOString(),
      startDate: newEvent.startDate ? new Date(newEvent.startDate).toISOString() : null,
      endDate: normalizedEndDate,
      eventStatus: normalizedEventStatus,
      systemId:
        newEvent.systemId !== undefined && newEvent.systemId !== null && newEvent.systemId !== ''
          ? Number(newEvent.systemId)
          : null,
    };
    if (!editingEvent) {
      const pendingComments = Array.isArray(eventComments) ? eventComments : [];
      const pendingDelays = Array.isArray(eventDelays) ? eventDelays : [];
      if (pendingComments.length) {
        payload.eventComments = pendingComments.map((comment) => ({
          comments: comment?.comments || comment?.comment || '',
          photos: Array.isArray(comment?.photos) ? comment.photos : [],
          userId: comment?.userId ?? comment?.user?.id ?? comment?.user?.userId ?? null,
        }));
      }
      if (pendingDelays.length) {
        payload.eventDelays = pendingDelays.map((delay) => ({
          eventDelayCodeId: delay?.eventDelayCodeId ?? delay?.eventDelayCode?.id ?? null,
          delayStartDate: delay?.delayStartDate ?? null,
          delayEndDate: delay?.delayEndDate ?? null,
        }));
      }
    }
    try {
      if (editingEvent) {
        await updateEvents(editingEvent.id, {
          ...payload,
          isModified: 1,
          updatedAt: new Date().toISOString(),
          updatedBy: userProfile?.id,
        });
      } else {
        await createEvents({
          ...payload,
          createdBy: userProfile?.id,
        });
      }
      setShowModal(false);
      await loadEvents();
    } catch (err) {
      console.error("Error saving event", err);
    }
  };

  const handleTaskDoubleClick = (task) => {
    const id = typeof task === 'object' ? task.id : task;
    const ev = (events || []).find((e) => String(e.id) === String(id));
    if (ev) openModal(ev);
  };

  const handleTaskSelect = (task, isSelected) => {
    if (isSelected) {
      const id = typeof task === 'object' ? task.id : task;
      const ev = (events || []).find((e) => String(e.id) === String(id));
      setSelectedEvent(ev || null);
    } else {
      setSelectedEvent(null);
    }
  };

  const handleTaskChange = async (task) => {
    const idx = (events || []).findIndex((e) => String(e.id) === String(task.id));
    if (idx === -1) return task;
    const existing = events[idx];
    const updated = {
      ...existing,
      plannedStartDate: task.start.toISOString(),
      plannedEndDate: task.end.toISOString(),
    };

    const start = updated.startDate
      ? new Date(updated.startDate)
      : new Date(updated.plannedStartDate);
    const end = updated.endDate
      ? new Date(updated.endDate)
      : new Date(updated.plannedEndDate);
    const durationHours = Math.abs(end - start) / (1000 * 60 * 60);
    const delayTotal = (updated.tblEventDelays || []).reduce(
      (sum, d) => sum + calcDelayDuration(d),
      0,
    );
    if (delayTotal >= durationHours) {
      alert('Total delay duration must be less than the event duration');
      return {
        ...task,
        start: existing.plannedStartDate
          ? new Date(existing.plannedStartDate)
          : new Date(existing.startDate),
        end: existing.plannedEndDate
          ? new Date(existing.plannedEndDate)
          : new Date(existing.endDate),
      };
    }

    const newEvents = [...events];
    newEvents[idx] = updated;
    setEvents(newEvents);
    try {
      await updateEvents(task.id, {
        plannedStartDate: updated.plannedStartDate,
        plannedEndDate: updated.plannedEndDate,
        updatedAt: new Date().toISOString(),
        updatedBy: userProfile?.id,
      });
    } catch (err) {
      console.error('Error updating task dates', err);
    }
    return task;
  };

  const handleAssetSearch = async (inputValue) => {
    try {
      const resp = await getPaginatedAssets({
        page: 1,
        pageSize: 10,
        search: inputValue || "",
      });
      const arr = resp.data || resp;
      return arr.map((a) => ({
        value: a.id,
        label: `${a.assetNumber} – ${a.assetDescription}`,
      }));
    } catch (err) {
      console.error("asset search", err);
      return [];
    }
  };

  const handleDropAssign = async (e) => {
    e.preventDefault();
    const selectedEventStatusId = Number(selectedEvent?.eventStatus);
    if (selectedEventStatusId === 1 || selectedEventStatusId === 2) {
      setDragWorkOrder(null);
      return;
    }
    const data = dragWorkOrder || JSON.parse(e.dataTransfer.getData('text/plain') || null);
    if (!data || !selectedEvent) return;
    try {
      await updateWorkOrder(data.id, { eventId: selectedEvent.id });
      setAvailableWorkOrders((prev) => prev.filter((w) => w.id !== data.id));
      setAssignedWorkOrders((prev) => [...prev, { ...data, eventId: selectedEvent.id }]);
      setEvents((prev) =>
        prev.map((ev) =>
          ev.id === selectedEvent.id
            ? { ...ev, workOrderCount: (ev.workOrderCount || 0) + 1 }
            : ev
        )
      );
    } catch (err) {
      console.error('assign work order', err);
    }
    setDragWorkOrder(null);
  };

  const handleDropUnassign = async (e) => {
    e.preventDefault();
    const selectedEventStatusId = Number(selectedEvent?.eventStatus);
    if (selectedEventStatusId === 1 || selectedEventStatusId === 2) {
      setDragWorkOrder(null);
      return;
    }
    const data = dragWorkOrder || JSON.parse(e.dataTransfer.getData('text/plain') || null);
    if (!data) return;
    // Ignore drops of work orders that are already unassigned
    if (!assignedWorkOrders.some((w) => w.id === data.id)) {
      setDragWorkOrder(null);
      return;
    }
    try {
      await updateWorkOrder(data.id, { eventId: null });
      setAssignedWorkOrders((prev) => prev.filter((w) => w.id !== data.id));
      setAvailableWorkOrders((prev) => [...prev, { ...data, eventId: null }]);
      setEvents((prev) =>
        prev.map((ev) =>
          ev.id === selectedEvent?.id
            ? { ...ev, workOrderCount: Math.max(0, (ev.workOrderCount || 1) - 1) }
            : ev
        )
      );
    } catch (err) {
      console.error('unassign work order', err);
    }
    setDragWorkOrder(null);
  };

  const renderWorkOrderCard = (wo, isUnassigned = false) => {
    const priorityColour = wo.priority?.colours?.bootstrapName || 'secondary';
    const selectedEventStatusId = Number(selectedEvent?.eventStatus);
    const canDragWorkOrders = selectedEventStatusId !== 1 && selectedEventStatusId !== 2;
    return (
      <div
        key={wo.id}
        className={`card mb-8 p-12 ${isUnassigned ? 'bg-neutral-200 text-muted' : ''}`}
        draggable={canDragWorkOrders}
        onDragStart={(e) => {
          if (!canDragWorkOrders) return;
          setDragWorkOrder(wo);
          e.dataTransfer.setData('text/plain', JSON.stringify(wo));
          e.dataTransfer.effectAllowed = 'move';
        }}
        onDragEnd={() => setDragWorkOrder(null)}
      >
        <div className="d-flex align-items-center gap-2">
          <span
            className={`d-inline-block rounded-1 bg-${priorityColour}`}
            style={{ width: '10px', height: '10px' }}
          ></span>
          <span className={`fw-bold ${isUnassigned ? 'text-muted' : 'text-black'}`}>{wo.workOrderNumber}</span>
          <span className={`text-sm ${isUnassigned ? 'text-muted' : 'text-black'}`}>{wo.workOrderTitle}</span>
        </div>
      </div>
    );
  };

  const handleAddDelay = () => {
    setEditingDelay(null);
    const defaultStartDate = computeDefaultDelayStartDate(newEvent, editingEvent);
    setDelayForm({
      eventDelayCodeId: '',
      delayStartDate: defaultStartDate ? toLocalInputFormat(defaultStartDate, true) : '',
      delayEndDate: '',
    });
    setDelayErrors({});
    setShowDelayModal(true);
  };

  const handleEditDelay = (delay) => {
    setEditingDelay(delay);
    setDelayForm({
      eventDelayCodeId: delay.eventDelayCodeId,
      delayStartDate: toLocalInputFormat(delay.delayStartDate, true),
      delayEndDate: toLocalInputFormat(delay.delayEndDate, true),
    });
    setDelayErrors({});
    setShowDelayModal(true);
  };

  const handleDeleteDelay = async (id) => {
    if (!window.confirm('Delete delay?')) return;
    try {
      if (editingEvent?.id) {
        await deleteEventDelay(id);
        await loadEventDelays(editingEvent.id);
        await loadEvents();
      } else {
        setEventDelays((prev) => (Array.isArray(prev) ? prev.filter((d) => d.id !== id) : []));
      }
    } catch (err) {
      console.error('Error deleting delay', err);
    }
  };

  const handleSaveDelay = async () => {
    const isBaseValid = validateDelayForm();
    const customErrors = validateDelayDateRange(
      delayForm.delayStartDate,
      delayForm.delayEndDate,
      newEvent,
      editingEvent,
    );
    if (Object.keys(customErrors).length > 0) {
      setDelayErrors((prev) => ({ ...prev, ...customErrors }));
    }
    if (!isBaseValid || Object.keys(customErrors).length > 0) return;

    const eventStart = newEvent.startDate
      ? new Date(newEvent.startDate)
      : new Date(newEvent.plannedStartDate);
    const eventEnd = newEvent.endDate
      ? new Date(newEvent.endDate)
      : new Date(newEvent.plannedEndDate);
    const delayStart = delayForm.delayStartDate ? new Date(delayForm.delayStartDate) : null;
    const delayEnd = delayForm.delayEndDate ? new Date(delayForm.delayEndDate) : null;

    const durationHours = Math.abs(eventEnd - eventStart) / (1000 * 60 * 60);
    const existingTotal = eventDelays.reduce((sum, d) => sum + calcDelayDuration(d), 0);
    const updatedTotal = editingDelay
      ? existingTotal - calcDelayDuration(editingDelay) + delayDuration
      : existingTotal + delayDuration;
    if (Number.isFinite(durationHours) && updatedTotal >= durationHours) {
      alert('Total delay duration must be less than the event duration');
      return;
    }
    const payload = {
      eventDelayCodeId: Number(delayForm.eventDelayCodeId),
      delayStartDate: delayStart ? delayStart.toISOString() : null,
      delayEndDate: delayEnd ? delayEnd.toISOString() : null,
    };
    try {
      if (editingEvent?.id) {
        const remotePayload = { ...payload, eventsId: editingEvent.id };
        if (editingDelay) {
          await updateEventDelay(editingDelay.id, remotePayload);
        } else {
          await createEventDelay(remotePayload);
        }
        await loadEventDelays(editingEvent.id);
        await loadEvents();
        setShowDelayModal(false);
      } else {
        const delayCode = delayCodes.find(
          (code) => String(code.id) === String(payload.eventDelayCodeId),
        );
        setEventDelays((prev) => {
          const existing = Array.isArray(prev) ? prev : [];
          const updatedDelay = {
            ...editingDelay,
            id: editingDelay?.id ?? Date.now(),
            ...payload,
            eventDelayCode: delayCode || editingDelay?.eventDelayCode || null,
          };
          const next = editingDelay
            ? existing.map((entry) => (entry.id === editingDelay.id ? updatedDelay : entry))
            : [updatedDelay, ...existing];
          return next;
        });
        setShowDelayModal(false);
      }
    } catch (err) {
      console.error('Error saving delay', err);
    }
  };

  const openAssignModal = () => {
    const unassigned = availableWorkOrders.filter(
      // eslint-disable-next-line no-mixed-operators
      (w) => !w.eventId && w.status?.name === 'Open' || w.status?.name === 'In Progress'
    );
    setAssignOptions(unassigned);
    setAssignSelected([]);
    setShowAssignModal(true);
  };

  const handleAssignSelected = async () => {
    try {
      await Promise.all(
        assignSelected.map((id) => updateWorkOrder(id, { eventId: editingEvent.id }))
      );
      const newly = assignOptions.filter((w) => assignSelected.includes(w.id));
      setAssignedWorkOrders((prev) => [
        ...prev,
        ...newly.map((w) => ({ ...w, eventId: editingEvent.id })),
      ]);
      setAvailableWorkOrders((prev) => prev.filter((w) => !assignSelected.includes(w.id)));
      setEvents((prev) =>
        prev.map((ev) =>
          ev.id === editingEvent.id
            ? { ...ev, workOrderCount: (ev.workOrderCount || 0) + newly.length }
            : ev
        )
      );
      setShowAssignModal(false);
    } catch (err) {
      console.error('assign work orders', err);
    }
  };

  const handleSelectAllAssign = () => {
    setAssignSelected(assignOptions.map((w) => w.id));
  };

  const handleDeselectAllAssign = () => {
    setAssignSelected([]);
  };

  useEffect(() => {
    setHeaderSearchContainer(document.getElementById('header-search-container'));
  }, []);

  useEffect(() => {
    if (!showAssignModal) return;
    if (assignDtRef.current) {
      assignDtRef.current.destroy();
      assignDtRef.current = null;
    }
    if (assignOptions.length > 0) {
      assignDtRef.current = $(assignTableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: { paginate: { first: '', last: '', next: '', previous: '' } },
      });
    }
    return () => {
      if (assignDtRef.current) {
        assignDtRef.current.destroy(true);
        assignDtRef.current = null;
      }
    };
  }, [assignOptions, showAssignModal]);

  useEffect(() => {
    assignDtRef.current?.search(assignSearch).draw();
  }, [assignSearch]);

  const handleDeleteWo = async (id) => {
    if (!window.confirm('Remove work order from event?')) return;
    try {
      await updateWorkOrder(id, { eventId: null });
      const removed = assignedWorkOrders.find((w) => w.id === id);
      setAssignedWorkOrders((prev) => prev.filter((w) => w.id !== id));
      if (removed)
        setAvailableWorkOrders((prev) => [...prev, { ...removed, eventId: null }]);
      setEvents((prev) =>
        prev.map((ev) =>
          ev.id === editingEvent.id
            ? { ...ev, workOrderCount: Math.max(0, (ev.workOrderCount || 1) - 1) }
            : ev
        )
      );
    } catch (err) {
      console.error('remove work order', err);
    }
  };

  const handleWoStatusChange = async (id, statusId) => {
    try {
      await updateWorkOrder(id, { statusId });
      const statusObj = workOrderStatuses.find((s) => String(s.value) === String(statusId));
      const status = statusObj
        ? {
          id: statusObj.value,
          name: statusObj.label,
          colours: { bootstrapName: statusObj.bootstrapName },
        }
        : null;
      setAssignedWorkOrders((prev) =>
        prev.map((w) =>
          w.id === id ? { ...w, statusId: Number(statusId), status } : w
        )
      );
    } catch (err) {
      console.error('update work order status', err);
    }
  };

  const handleCompleteAllWork = async () => {
    const complete = workOrderStatuses.find((s) => s.label.toLowerCase() === 'complete');
    if (!complete) return;
    const { eventStart, eventEnd } = getEventRange(newEvent);
    const hasOutsideRange = hasWorkOrdersOutsideEventRange(assignedWorkOrders, eventStart, eventEnd);
    if (hasOutsideRange) {
      const shouldAlignDates = window.confirm(
        'One or more Work Order start and end dates falls outside the event start / end date. Do you want to align these work orders to the event start and end date / time?',
      );
      if (!shouldAlignDates) {
        return;
      }
    }
    try {
      await Promise.all(
        assignedWorkOrders.map((w) =>
          updateWorkOrder(
            w.id,
            buildCompleteWorkOrderPayload(
              w,
              complete.value,
              eventStart,
              eventEnd,
              hasOutsideRange,
            ),
          ),
        ),
      );
      setAssignedWorkOrders((prev) =>
        prev.map((w) => ({
          ...w,
          ...buildCompleteWorkOrderPayload(
            w,
            complete.value,
            eventStart,
            eventEnd,
            hasOutsideRange,
          ),
          statusId: complete.value,
          status: complete,
        }))
      );
    } catch (err) {
      console.error('complete all work', err);
    }
  };

  const openEditWoModal = (wo) => {
    setEditingWo(wo);
    setEditWoForm({
      statusId: wo.statusId || '',
      startedAt: wo.startedAt
        ? wo.startedAt.slice(0, 16)
        : (editingEvent.startDate || editingEvent.plannedStartDate || '').slice(0, 16),
      completedAt: wo.completedAt
        ? wo.completedAt.slice(0, 16)
        : (editingEvent.endDate || editingEvent.plannedEndDate || '').slice(0, 16),
      failureId: wo.failureId || '',
      causeId: wo.causeId || '',
      symptom: wo.symptom || '',
      notes: wo.notes || '',
    });
    setEditWoErrors({});
    setShowEditWoModal(true);
  };

  const handleSaveEditWo = async () => {
    const statusName = workOrderStatuses.find(
      (s) => String(s.value) === String(editWoForm.statusId)
    )?.label?.toLowerCase();
    const errs = {};
    if (!editWoForm.statusId) errs.statusId = 'Required';
    if (!editWoForm.startedAt) errs.startedAt = 'Required';
    if (statusName === 'complete' && !editWoForm.completedAt) errs.completedAt = 'Required';
    setEditWoErrors(errs);
    if (Object.keys(errs).length > 0) return;
    const payload = {
      statusId: Number(editWoForm.statusId),
      startedAt: editWoForm.startedAt ? new Date(editWoForm.startedAt).toISOString() : null,
      completedAt: editWoForm.completedAt
        ? new Date(editWoForm.completedAt).toISOString()
        : null,
      failureId: editWoForm.failureId || null,
      causeId: editWoForm.causeId || null,
      symptom: editWoForm.symptom || '',
      notes: editWoForm.notes || '',
    };
    try {
      await updateWorkOrder(editingWo.id, payload);
      const statusObj = workOrderStatuses.find(
        (s) => String(s.value) === String(payload.statusId)
      );
      const status = statusObj
        ? {
          id: statusObj.value,
          name: statusObj.label,
          colours: { bootstrapName: statusObj.bootstrapName },
        }
        : null;
      setAssignedWorkOrders((prev) =>
        prev.map((w) => (w.id === editingWo.id ? { ...w, ...payload, status } : w))
      );
      setShowEditWoModal(false);
    } catch (err) {
      console.error('save work order', err);
    }
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", onDrag);
      window.addEventListener("mouseup", stopDrag);
    }
    return () => {
      window.removeEventListener("mousemove", onDrag);
      window.removeEventListener("mouseup", stopDrag);
    };
  }, [isDragging]);

  useEffect(() => {
    window.addEventListener('mousemove', handleGanttMouseMove);
    window.addEventListener('mouseup', stopGanttPan);
    return () => {
      window.removeEventListener('mousemove', handleGanttMouseMove);
      window.removeEventListener('mouseup', stopGanttPan);
    };
  }, []);

  return (
    <>
      {headerSearchContainer &&
        createPortal(
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search..."
            value={assignSearch}
            onChange={(e) => setAssignSearch(e.target.value)}
          />,
          headerSearchContainer,
        )}
      <div className="card h-100 p-0">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex flex-wrap align-items-top gap-2">
            {renderFilterControls()}
            <div className="btn-group ms-3 mt-24" style={{ height: "45px" }}>
              <button className={`btn btn-sm ${viewMode === 'day' ? 'btn-primary' : 'btn-light'}`} onClick={() => setViewMode('day')}>Day</button>
              <button className={`btn btn-sm ${viewMode === 'week' ? 'btn-primary' : 'btn-light'}`} onClick={() => setViewMode('week')}>Week</button>
              <button className={`btn btn-sm ${viewMode === 'month' ? 'btn-primary' : 'btn-light'}`} onClick={() => setViewMode('month')}>Month</button>
              <button className={`btn btn-sm ${viewMode === 'year' ? 'btn-primary' : 'btn-light'}`} onClick={() => setViewMode('year')}>Year</button>
            </div>
            <div className="btn-group ms-2 mt-24" style={{ height: "45px" }}>
              <button className="btn btn-sm btn-light" onClick={() => adjustDate(-1)}>
                <Icon icon="mage:chevron-left" />
              </button>
              <button className="btn btn-sm btn-light" onClick={() => adjustDate(1)}>
                <Icon icon="mage:chevron-right" />
              </button>
            </div>
          </div>
          <button className="btn btn-primary text-sm btn-sm px-12 py-12 radius-4" style={{ width: "118px" }} onClick={() => openModal()}>
            <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" /> Add Event
          </button>
        </div>
        <div className="timeline-scrollbar-wrapper m-16 d-flex align-items-center justify-content-end gap-2">
          <span className="text-sm text-secondary-light fw-semibold me-6">Scroll</span>
          <input
            type="range"
            min="0"
            max="1000"
            step="1"
            value={timelineScrollValue}
            onChange={handleTimelineScrollBar}
            className="timeline-scrollbar"
          />
        </div>
        <div
          className="card-body p-0 d-flex flex-column"
          style={{ height: "calc(100vh - 265px)" }}
          ref={containerRef}
        >
          <div
            style={{ flexBasis: `${split}%`, overflow: 'auto' }}
            ref={ganttRef}
            onMouseDown={handleGanttMouseDown}
          >
            {tasks && tasks.length > 0 && (
              <Timeline
                groups={groups}
                items={tasks}
                visibleTimeStart={visibleTime.start}
                visibleTimeEnd={visibleTime.end}
                sidebarWidth={350}
                lineHeight={40}
                itemHeightRatio={0.25}
                onItemMove={(id, dragTime) => {
                  const item = tasks.find((t) => String(t.id) === String(id));
                  if (!item) return;
                  const diff = new Date(item.end_time) - new Date(item.start_time);
                  handleTaskChange({ id, start: new Date(dragTime), end: new Date(dragTime + diff) });
                }}
                onItemResize={(id, time, edge) => {
                  const item = tasks.find((t) => String(t.id) === String(id));
                  if (!item) return;
                  const start = edge === 'left' ? new Date(time) : new Date(item.start_time);
                  const end = edge === 'left' ? new Date(item.end_time) : new Date(time);
                  handleTaskChange({ id, start, end });
                }}
                onItemDoubleClick={handleTaskDoubleClick}
                onItemSelect={(id) => handleTaskSelect(id, true)}
                onCanvasClick={() => handleTaskSelect(null, false)}
                itemTouchSendsClick={false}
                stackItems
                onTimeChange={(visibleTimeStart, visibleTimeEnd, updateScrollCanvas) => {
                  setVisibleTime({ start: visibleTimeStart, end: visibleTimeEnd });
                  updateScrollCanvas(visibleTimeStart, visibleTimeEnd);
                }}
              />
            )}
          </div>
          <div className="gantt-divider" onMouseDown={startDrag} />
          <div style={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {!selectedEvent && (
              <div className="d-flex justify-content-center align-items-center h-100">
                Select an event to view work orders
              </div>
            )}
            {selectedEvent && (
              <>
                <div className="row m-0">
                  <div className="col-12 fw-bold p-2 border-bottom text-center fs-6">{selectedEvent.name} - {selectedEvent.asset.assetNumber} - {selectedEvent.asset.assetDescription}</div>
                </div>
                <div className="row m-0 flex-grow-1 h-100 work-orders-row">
                  <div
                    className="col-md-4 border-end d-flex flex-column work-orders-column h-100"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleDropUnassign}
                  >
                    <span className="p-2 fw-bold">Unassigned Asset Work Orders</span>
                    <div className="work-orders-list">
                      {availableWorkOrders.map((wo) => renderWorkOrderCard(wo, true))}
                    </div>
                  </div>
                  <div
                    id="event-workorders"
                    className="col-md-4 border-end d-flex flex-column event-workorders-column h-100"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleDropAssign}
                  >
                    <span className="p-2 fw-bold">Event Work Orders</span>
                    <div className="work-orders-list">
                      {assignedWorkOrders.map((wo) => renderWorkOrderCard(wo, false))}
                    </div>
                  </div>
                  <div
                    className="col-md-4 d-flex flex-column delays-column h-100"
                  >
                    <span className="p-2 fw-bold">Delays</span>
                    <div className="table-responsive">
                      <table className="table dataTable mb-3 w-100">
                        <tbody>
                          {eventDelays.map((d) => (
                            <tr key={d.id}>
                              <td>
                                <strong>{d.eventDelayCode?.code}</strong> - {d.eventDelayCode?.name}
                              </td>
                              <td>{formatDateTime(d.delayStartDate)}</td>
                              <td>{formatDateTime(d.delayEndDate)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <EventEditModal
          show={showModal}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
          editingEvent={editingEvent}
          newEvent={newEvent}
          setNewEvent={setNewEvent}
          newEventErrors={newEventErrors}
          statusOptions={statusOptions}
          eventTypeOptions={eventTypeOptions}
          assetOptions={assetOptions}
          handleAssetSearch={handleAssetSearch}
          assignedWorkOrders={assignedWorkOrders}
          workOrderStatuses={workOrderStatuses}
          onCompleteAllWork={handleCompleteAllWork}
          onOpenAssignModal={openAssignModal}
          onWorkOrderStatusChange={handleWoStatusChange}
          onDeleteWorkOrder={handleDeleteWo}
          onEditWorkOrder={openEditWoModal}
          eventDelays={eventDelays}
          onAddDelay={handleAddDelay}
          onEditDelay={handleEditDelay}
          onDeleteDelay={handleDeleteDelay}
          formatDateTime={formatDateTime}
          eventComments={eventComments}
          onEventCommentsChange={setEventComments}
        />
        {showDelayModal && (
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editingDelay ? 'Edit Delay' : 'Add Delay'}</h5>
                  <button className="btn-close" onClick={() => setShowDelayModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Delay Code</label>
                    <select
                      className={`form-select ${delayErrors.eventDelayCodeId ? 'is-invalid' : ''}`}
                      value={delayForm.eventDelayCodeId || ''}
                      onChange={(e) =>
                        setDelayForm({ ...delayForm, eventDelayCodeId: e.target.value })
                      }
                    >
                      <option value="">Select...</option>
                      {delayCodes.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.code} - {c.name}
                        </option>
                      ))}
                    </select>
                    {delayErrors.eventDelayCodeId && (
                      <div className="invalid-feedback d-block">{delayErrors.eventDelayCodeId}</div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Start Date</label>
                    <input
                      type="datetime-local"
                      className={`form-control ${delayErrors.delayStartDate ? 'is-invalid' : ''}`}
                      value={delayForm.delayStartDate || ''}
                      onChange={(e) =>
                        setDelayForm({ ...delayForm, delayStartDate: e.target.value })
                      }
                    />
                    {delayErrors.delayStartDate && (
                      <div className="invalid-feedback d-block">{delayErrors.delayStartDate}</div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">End Date</label>
                    <input
                      type="datetime-local"
                      className={`form-control ${delayErrors.delayEndDate ? 'is-invalid' : ''}`}
                      value={delayForm.delayEndDate || ''}
                      onChange={(e) =>
                        setDelayForm({ ...delayForm, delayEndDate: e.target.value })
                      }
                    />
                    {delayErrors.delayEndDate && (
                      <div className="invalid-feedback d-block">{delayErrors.delayEndDate}</div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowDelayModal(false)}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={handleSaveDelay}>
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {showAssignModal && (
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Assign Work Orders</h5>
                  <button className="btn-close" onClick={() => setShowAssignModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="d-flex justify-content-end align-items-center mb-3 gap-2">
                    <button className="btn btn-primary btn-sm" onClick={handleSelectAllAssign}>Select All</button>
                    <button className="btn btn-secondary btn-sm" onClick={handleDeselectAllAssign}>Deselect All</button>
                  </div>
                  <div className="table-responsive">
                    <table className="table bordered-table mb-0 w-100 rounded-0" ref={assignTableRef}>
                      <thead>
                        <tr>
                          <th></th>
                          <th>Number</th>
                          <th>Title</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assignOptions.map((wo) => (
                          <tr key={wo.id}>
                            <td>
                              <div className="form-check style-check d-flex justify-content-center">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={assignSelected.includes(wo.id)}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setAssignSelected((prev) =>
                                      checked ? [...prev, wo.id] : prev.filter((id) => id !== wo.id)
                                    );
                                  }}
                                />
                              </div>
                            </td>
                            <td>{wo.workOrderNumber}</td>
                            <td>{wo.workOrderTitle}</td>
                          </tr>
                        ))}
                        {assignOptions.length === 0 && (
                          <tr>
                            <td colSpan="3" className="text-center py-2">No Work Orders Available</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowAssignModal(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleAssignSelected}>Assign</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {showEditWoModal && (
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit Work Order</h5>
                  <button className="btn-close" onClick={() => setShowEditWoModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-3 fw-bold">
                    #{editingWo?.workOrderNumber} - {editingWo?.workOrderTitle}
                  </div>
                  <div className="row g-3">
                    {[
                      { field: 'startedAt', label: 'Started At', type: 'datetime', size: 6 },
                      { field: 'completedAt', label: 'Completed At', type: 'datetime', size: 6 },
                      {
                        field: 'statusId', label: 'Status', type: 'select', size: 12, api: 'work-order-status', displayField: 'name', required: true, formatOptionLabel: (opt) => (
                          <div className="d-flex align-items-center gap-2">
                            <span className={`d-inline-block rounded-1 bg-${opt.bootstrapName}`} style={{ width: '10px', height: '10px' }}></span>
                            {opt.label}
                          </div>
                        )
                      },
                      { field: 'failureId', label: 'Failure', type: 'select', size: 6, api: 'failure-codes', displayField: 'name' },
                      { field: 'causeId', label: 'Cause', type: 'select', size: 6, api: 'cause-codes', displayField: 'name' },
                      { field: 'symptom', label: 'Symptom', type: 'text', size: 12 },
                      { field: 'notes', label: 'Completion Notes', type: 'textarea', size: 12 },
                    ].map((f) => {
                      if (f.field === 'completedAt') {
                        const statusName = workOrderStatuses.find((s) => String(s.value) === String(editWoForm.statusId))?.label?.toLowerCase();
                        if (statusName !== 'complete') return null;
                      }
                      return renderEditWoField(f);
                    })}
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowEditWoModal(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleSaveEditWo}>Save</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default VisualPlannerLayer;
